import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  onSnapshot, serverTimestamp, getDoc,
  query, limit, arrayUnion, setDoc, writeBatch,
} from 'firebase/firestore';
import { loadTotals, loadHsTotals } from './seriesTotals';
import { db } from '../firebase';
// Note : Firebase Storage non utilisé — photos stockées en Firestore base64

// ─── Sync temps réel (onSnapshot) ────────────────────────────────────────────
// Plafond de sécurité large (l'ancien plafond de 500 tronquait silencieusement
// les grosses collections). Si on l'atteint, onChange reçoit truncated=true.
export const BD_LIMIT = 5000;
export const subscribeBDs = (userId, onChange, onError) => {
  const q = query(collection(db, 'users', userId, 'bds'), limit(BD_LIMIT));
  return onSnapshot(q,
    snap => onChange(snap.docs.map(d => ({ id: d.id, ...d.data() })), snap.size >= BD_LIMIT),
    onError
  );
};

export const subscribeWishlist = (userId, onChange, onError) => {
  return onSnapshot(collection(db, 'users', userId, 'wishlist'),
    snap => onChange(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    onError
  );
};

// ─── BD ──────────────────────────────────────────────────────────────────────
export const addBD = async (userId, data) => {
  const { photos, ...rest } = data;
  const docRef = await addDoc(collection(db, 'users', userId, 'bds'), {
    ...rest, photos: [],
    createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
  });
  return docRef.id;
};

// ─── Ajout groupé (ex : les 72 tomes d'une série d'un coup) ──────────────────
// Écrit par lots de 400 (marge sous la limite Firestore de 500 opérations
// par batch), chaque lot étant atomique.
export const addBDsBatch = async (userId, items) => {
  const CHUNK = 400;
  const ids = [];
  for (let i = 0; i < items.length; i += CHUNK) {
    const slice = items.slice(i, i + CHUNK);
    const batch = writeBatch(db);
    const refs = slice.map(() => doc(collection(db, 'users', userId, 'bds')));
    slice.forEach((data, idx) => {
      const { photos, ...rest } = data;
      batch.set(refs[idx], {
        ...rest, photos: [],
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
    });
    await batch.commit();
    ids.push(...refs.map(r => r.id));
  }
  return ids;
};

export const updateBD = async (userId, bdId, data) => {
  await updateDoc(doc(db, 'users', userId, 'bds', bdId), {
    ...data, updatedAt: serverTimestamp(),
  });
};

export const deleteBD = async (userId, bdId) => {
  // Supprimer aussi toutes les photos de la sous-collection
  const bdDoc = await getDoc(doc(db, 'users', userId, 'bds', bdId));
  if (bdDoc.exists()) {
    const photoIds = (bdDoc.data().photos || []).map(p => p.id).filter(Boolean);
    await Promise.all(
      photoIds.map(pid =>
        deleteDoc(doc(db, 'users', userId, 'bds', bdId, 'photos', pid)).catch(() => {})
      )
    );
  }
  await deleteDoc(doc(db, 'users', userId, 'bds', bdId));
};

// ─── Corbeille (suppression douce, récupérable 30 jours) ─────────────────────
const TRASH_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

export const softDeleteBD = async (userId, bdId) => {
  const ref = doc(db, 'users', userId, 'bds', bdId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  await setDoc(doc(db, 'users', userId, 'trash', bdId), {
    ...snap.data(), deletedAt: serverTimestamp(),
  });
  await deleteDoc(ref);
};

export const subscribeTrash = (userId, onChange, onError) => {
  const q = query(collection(db, 'users', userId, 'trash'), limit(500));
  return onSnapshot(q, snap => onChange(snap.docs.map(d => ({ id: d.id, ...d.data() }))), onError);
};

export const restoreBD = async (userId, bdId) => {
  const ref = doc(db, 'users', userId, 'trash', bdId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Cet album n'est plus dans la corbeille.");
  const { deletedAt, ...data } = snap.data();
  await setDoc(doc(db, 'users', userId, 'bds', bdId), { ...data, updatedAt: serverTimestamp() });
  await deleteDoc(ref);
  return data;
};

export const permanentlyDeleteBD = async (userId, bdId) => {
  await deleteDoc(doc(db, 'users', userId, 'trash', bdId));
};

// Purge silencieuse des éléments de plus de 30 jours — appelée à l'ouverture de la corbeille
export const purgeOldTrash = async (userId, trashItems) => {
  const cutoff = Date.now() - TRASH_RETENTION_MS;
  const old = trashItems.filter(t => {
    const ms = t.deletedAt?.toDate ? t.deletedAt.toDate().getTime() : 0;
    return ms && ms < cutoff;
  });
  await Promise.all(old.map(t => deleteDoc(doc(db, 'users', userId, 'trash', t.id)).catch(() => {})));
  return old.length;
};
export const TRASH_RETENTION_DAYS = 30;

// ─── Photos — stockées en Firestore base64 (sans Firebase Storage) ───────────
//
// Architecture : /users/{uid}/bds/{bdId}/photos/{photoId}
// Chaque document = une photo compressée en base64
// Avantages :
//   ✓ Pas besoin de Firebase Storage (plan Blaze non requis)
//   ✓ 1 Go gratuit Firestore = ~3000 photos
//   ✓ Même règles de sécurité que les BDs
//   ✓ Fonctionne offline avec le cache Firestore

export const compressImage = (file, maxW = 800, quality = 0.72) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      // Réduction agressive pour Firestore : max 800px, qualité 72%
      // → ~80-120 Ko compressé → ~110-160 Ko en base64 (< 1 Mo max Firestore)
      const scale = Math.min(1, maxW / Math.max(img.width, img.height));
      const [w, h] = [Math.round(img.width * scale), Math.round(img.height * scale)];
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        blob => {
          if (!blob) { reject(new Error('Compression impossible')); return; }
          // Convertir en base64
          const reader = new FileReader();
          reader.onload = e => resolve({
            dataUrl: e.target.result,
            sizeKo: Math.round(blob.size / 1024),
          });
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        },
        'image/jpeg', quality
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Fichier image invalide ou format non supporté (HEIC non supporté sur ce navigateur)')); };
    img.src = url;
  });

export const uploadPhoto = async (userId, bdId, file, onProgress) => {
  onProgress?.(10); // Début compression

  const { dataUrl, sizeKo } = await compressImage(file);

  // Vérification taille avant envoi (limite Firestore = 1 Mo par doc)
  if (sizeKo > 750) {
    // Re-compresser plus agressivement si trop grand
    const result2 = await compressImage(file, 600, 0.65);
    if (result2.sizeKo > 750) {
      throw new Error(`Image trop grande après compression (${result2.sizeKo} Ko). Essaie avec une photo moins haute résolution.`);
    }
    onProgress?.(50);
    const photoId = Date.now().toString();
    const photoRef = doc(db, 'users', userId, 'bds', bdId, 'photos', photoId);
    await setDoc(photoRef, {
      dataUrl: result2.dataUrl,
      uploadedAt: new Date().toISOString(),
      sizeKo: result2.sizeKo,
    });
    const photo = { id: photoId, url: result2.dataUrl, uploadedAt: new Date().toISOString() };
    await updateDoc(doc(db, 'users', userId, 'bds', bdId), {
      photos: arrayUnion(photo), updatedAt: serverTimestamp(),
    });
    onProgress?.(100);
    return photo;
  }

  onProgress?.(60); // Compression terminée, envoi Firestore

  const photoId = Date.now().toString();
  const photoRef = doc(db, 'users', userId, 'bds', bdId, 'photos', photoId);
  await setDoc(photoRef, {
    dataUrl,
    uploadedAt: new Date().toISOString(),
    sizeKo,
  });

  // Référence légère dans le doc principal (juste l'id + dataUrl pour affichage)
  const photo = { id: photoId, url: dataUrl, uploadedAt: new Date().toISOString() };
  await updateDoc(doc(db, 'users', userId, 'bds', bdId), {
    photos: arrayUnion(photo), updatedAt: serverTimestamp(),
  });

  onProgress?.(100);
  return photo;
};

export const deletePhoto = async (userId, bdId, photoId, dataUrl) => {
  // Supprimer le doc de la sous-collection si on a l'id
  if (photoId) {
    await deleteDoc(doc(db, 'users', userId, 'bds', bdId, 'photos', photoId)).catch(() => {});
  }
  // Retirer du tableau photos du doc principal
  const bdDoc = await getDoc(doc(db, 'users', userId, 'bds', bdId));
  const photos = (bdDoc.data()?.photos || []).filter(p =>
    photoId ? p.id !== photoId : p.url !== dataUrl
  );
  await updateDoc(doc(db, 'users', userId, 'bds', bdId), {
    photos, updatedAt: serverTimestamp(),
  });
  return photos;
};

// ─── Réordonnancement des photos ─────────────────────────────────────────────
// Écrit directement le nouvel ordre dans le doc principal (même doc que celui
// mis à jour par uploadPhoto/deletePhoto, pour rester cohérent avec eux).
export const reorderPhotos = async (userId, bdId, orderedPhotos) => {
  await updateDoc(doc(db, 'users', userId, 'bds', bdId), {
    photos: orderedPhotos, updatedAt: serverTimestamp(),
  });
};

// ─── Wishlist ─────────────────────────────────────────────────────────────────
export const addWish = async (userId, data) => {
  const docRef = await addDoc(collection(db, 'users', userId, 'wishlist'), {
    ...data, createdAt: serverTimestamp(),
  });
  return docRef.id;
};
export const updateWish  = (userId, wishId, data) =>
  updateDoc(doc(db,'users',userId,'wishlist',wishId), {...data, updatedAt:serverTimestamp()});
export const deleteWish  = (userId, wishId) =>
  deleteDoc(doc(db,'users',userId,'wishlist',wishId));
export const wishToBD = async (userId, wish) => {
  const { id, createdAt, updatedAt, priorite, ...rest } = wish;
  const newId = await addBD(userId, { ...rest, statut:'garder' });
  await deleteWish(userId, id);
  return newId;
};

// ─── Stats ────────────────────────────────────────────────────────────────────
export const computeStats = (bds) => {
  const total = bds.length;
  const series = [...new Set(bds.map(b=>b.serie).filter(Boolean))];
  const valeurAchat = bds.reduce((s,b)=>s+(parseFloat(b.prixAchat)||0), 0);
  const valeurVente = bds.filter(b=>b.statut==='vendre').reduce((s,b)=>s+(parseFloat(b.prixVente)||0), 0);
  const parStatut = {};
  bds.forEach(b => { parStatut[b.statut] = (parStatut[b.statut]||0)+1; });
  const lus = bds.filter(b=>b.lu).length;
  return { total, series: series.length, valeurAchat, valeurVente, parStatut, lus };
};

// ─── Export CSV ───────────────────────────────────────────────────────────────
export const exportCSV = (bds) => {
  const cols = ['serie','numero','titre','scenariste','dessinateur','coloriste','auteur',
    'editeur','collection','dateParution','isbn','format','langue','genre','nbPages',
    'etatGeneral','statut','note','prixAchat','prixVente','dateAchat','lieuAchat',
    'dateLecture','lieu','emplacement','lu','eo','dedicace','numerote','defauts','notes'];
  const escape = v => {
    if (v==null) return '';
    const s = Array.isArray(v) ? v.join(' | ') : String(v);
    return s.includes(';')||s.includes('"')||s.includes('\n') ? `"${s.replace(/"/g,'""')}"` : s;
  };
  const csv = [cols.join(';'), ...bds.map(b=>cols.map(c=>escape(b[c])).join(';'))].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob(['\uFEFF'+csv], {type:'text/csv;charset=utf-8'}));
  a.download = `bulletheque_${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
};

// ─── Recherche ISBN ───────────────────────────────────────────────────────────
// ─── Mapping catégories API → nos genres ─────────────────────────────────────
const mapGenre = (categories = []) => {
  const s = categories.join(' ').toLowerCase();
  if (/shonen|shōnen|shounen/i.test(s))     return 'Manga – Shōnen';
  if (/shojo|shōjo|shoujo/i.test(s))        return 'Manga – Shōjo';
  if (/seinen/i.test(s))                     return 'Manga – Seinen';
  if (/josei/i.test(s))                      return 'Manga – Josei';
  if (/manga|manhwa|manhua/i.test(s))        return 'Manga – Shōnen';
  if (/comic|superhero|marvel|dc /i.test(s)) return 'Comic book américain';
  if (/roman graphique|graphic novel/i.test(s)) return 'Roman graphique';
  if (/humour|humor|gag|funny/i.test(s))    return 'Humour / Gags';
  if (/horror|horreur/i.test(s))            return 'Horreur / Gore';
  if (/fantasy|fantastique/i.test(s))       return 'Fantastique / Fantasy';
  if (/science.fiction|sci-fi|anticipation/i.test(s)) return 'Anticipation / Dystopie';
  if (/policier|thriller|noir|detective/i.test(s)) return 'Policier / Noir';
  if (/guerre|war/i.test(s))               return 'Guerre';
  if (/histoire|historical|péplum/i.test(s)) return 'Histoire / Péplum';
  if (/biograph|memoir/i.test(s))           return 'Biographie / Mémoires';
  if (/jeunesse|children|kids|youth/i.test(s)) return 'Jeunesse';
  if (/western/i.test(s))                   return 'Western';
  if (/espionnage|spy|thriller/i.test(s))   return 'Espionnage / Thriller';
  return '';
};

// Mapping langue API → nos libellés
const mapLangue = (lang) => {
  const map = { fr:'Français', en:'Anglais', de:'Allemand', es:'Espagnol',
                it:'Italien', ja:'Japonais', nl:'Néerlandais', pt:'Portugais',
                ar:'Arabe', zh:'Chinois' };
  return map[lang] || '';
};

// Mapping éditeur API → nos éditeurs connus (normalisation)
const normaliseEditeur = (pub = '') => {
  const p = pub.toLowerCase();
  const map = [
    [/dargaud/,'Dargaud'], [/dupuis/,'Dupuis'], [/lombard/,'Le Lombard'],
    [/casterman/,'Casterman'], [/glenat|glénat/,'Glénat'], [/delcourt/,'Delcourt'],
    [/kana/,'Kana'], [/pika/,'Pika Édition'], [/kurokawa/,'Kurokawa'],
    [/ankama/,'Ankama'], [/ki.oon/,'Ki-oon'], [/akata/,'Akata'],
    [/bamboo/,'Bamboo'], [/panini/,'Panini Comics'], [/albert.ren/,'Albert René'],
    [/hachette/,'Hachette'], [/jungle/,'Jungle'], [/milan/,'Milan'],
    [/fluide.glacial/,'Fluide Glacial'], [/futuropolis/,'Futuropolis'],
    [/humanoïdes|humanoides/,'Les Humanoïdes Associés'],
  ];
  for (const [re, name] of map) { if (re.test(p)) return name; }
  // Retourner tel quel si pas dans la liste (sera saisi comme texte libre)
  return pub;
};

// Détecter série + numéro depuis le titre (ex: "Naruto, tome 5" ou "Astérix T.12")
const parseSerieTitre = (titre = '', subtitle = '') => {
  const full = titre + (subtitle ? ' ' + subtitle : '');

  // Patterns : "Série, tome N", "Série T.N", "Série - Tome N", "Série (tome N)"
  const patterns = [
    /^(.+?)[,\s–-]+(?:tome|vol\.?|volume|t\.?)\s*(\d+)/i,
    /^(.+?)\s+(?:tome|vol\.?|volume|t\.?)\s*(\d+)/i,
    /^(.+?)\s+\((?:tome|vol\.?|volume|t\.?)\s*(\d+)\)/i,
    /^(.+?)\s+#(\d+)/i,
    /^(.+?)\s+(\d+)$/,  // "Naruto 5" (numéro en fin)
  ];

  for (const re of patterns) {
    const m = full.match(re);
    if (m) {
      const serie = m[1].trim().replace(/[,\s–-]+$/, '');
      const numero = parseInt(m[2]);
      if (numero > 0 && numero < 500 && serie.length > 1) {
        return { serie, numero: String(numero), titrePropre: titre };
      }
    }
  }
  return { serie: '', numero: '', titrePropre: titre };
};

const mapGoogleVolume = (vol, fallbackIsbn = '') => {
  const { serie, numero, titrePropre } = parseSerieTitre(vol.title || '', vol.subtitle || '');
  const allAuthors = (vol.authors || []).filter(Boolean);
  const isbn13 = vol.industryIdentifiers?.find(i => i.type === 'ISBN_13')?.identifier;
  return {
    titre:        titrePropre,
    serie:        serie,
    numero:       numero,
    auteur:       allAuthors.length === 1 ? allAuthors[0] : '',
    scenariste:   allAuthors.length >= 2  ? allAuthors[0] : '',
    dessinateur:  allAuthors.length >= 2  ? allAuthors[1] : '',
    editeur:      normaliseEditeur(vol.publisher || ''),
    dateParution: vol.publishedDate?.slice(0, 4) || '',
    nbPages:      vol.pageCount ? String(vol.pageCount) : '',
    genre:        mapGenre(vol.categories || []),
    langue:       mapLangue(vol.language),
    isbn:         fallbackIsbn || isbn13 || '',
    coverUrl:     vol.imageLinks?.thumbnail || vol.imageLinks?.smallThumbnail || '',
  };
};

export const fetchByISBN = async (isbn) => {
  const clean = isbn.replace(/[\s-]/g,'');

  // ── 1. Open Library ────────────────────────────────────────────────────────
  try {
    const r = await fetch(`https://openlibrary.org/api/books?bibkeys=ISBN:${clean}&format=json&jscmd=data`);
    const d = await r.json();
    const book = d[`ISBN:${clean}`];
    if (book) {
      const { serie, numero, titrePropre } = parseSerieTitre(book.title || '');
      const allAuthors = (book.authors || []).map(a => a.name).filter(Boolean);
      return {
        titre:        titrePropre,
        serie:        serie,
        numero:       numero,
        // Si 2+ auteurs BD franco-belge : souvent scénariste + dessinateur
        auteur:       allAuthors.length === 1 ? allAuthors[0] : '',
        scenariste:   allAuthors.length >= 2  ? allAuthors[0] : '',
        dessinateur:  allAuthors.length >= 2  ? allAuthors[1] : '',
        editeur:      normaliseEditeur(book.publishers?.[0]?.name || ''),
        dateParution: book.publish_date?.match(/\d{4}/)?.[0] || '',
        nbPages:      book.number_of_pages ? String(book.number_of_pages) : '',
        isbn:         clean,
      };
    }
  } catch {}

  // ── 2. Google Books (fallback) ─────────────────────────────────────────────
  try {
    const r = await fetch(`https://www.googleapis.com/books/v1/volumes?q=isbn:${clean}`);
    const d = await r.json();
    const vol = d.items?.[0]?.volumeInfo;
    if (vol) { const { coverUrl, ...rest } = mapGoogleVolume(vol, clean); return rest; }
  } catch {}

  return null;
};

// ─── Estimation de valeur (basée sur prix de marché, sans prixAchat) ─────────
export const estimerValeur = async (bd) => {
  const PRIX_MARCHE = {
    'Manga':           { occasion: 4.0 },
    'Manga adulte':    { occasion: 5.0 },
    'BD franco-belge': { occasion: 7.0 },
    'Comics':          { occasion: 8.0 },
    'Roman graphique': { occasion: 9.0 },
    'Humour':          { occasion: 6.0 },
    'Jeunesse':        { occasion: 5.5 },
    'default':         { occasion: 6.5 },
  };
  const base = (PRIX_MARCHE[bd.genre] || PRIX_MARCHE['default']).occasion;

  const COEF_ETAT = { neuf:1.0, tres_bon:0.70, bon:0.50, acceptable:0.28, mauvais:0.12 };
  const coefEtat = COEF_ETAT[bd.etatGeneral] || 0.55;

  const annee = parseInt(bd.dateParution) || new Date().getFullYear();
  const age = new Date().getFullYear() - annee;
  const coefAge = age>40?2.2 : age>25?1.5 : age>15?1.2 : age>5?1.0 : 0.75;

  const coefSpecial = (bd.eo?2.5:1) * (bd.dedicace?2.2:1) * (bd.numerote?1.6:1);

  const prixBase = base * coefEtat * coefAge * coefSpecial;
  const estim = Math.round(prixBase * 10) / 10;
  const low   = Math.round(prixBase * 0.75 * 10) / 10;
  const high  = Math.round(prixBase * 1.40 * 10) / 10;

  const etatLabels = { neuf:'Neuf', tres_bon:'Très bon état', bon:'Bon état', acceptable:'État acceptable', mauvais:'Mauvais état' };
  const sources = [
    { label: 'Base marché ' + (bd.genre||'BD'), detail: `Prix d'occasion moyen : ${base} €`, type:'market' },
    { label: 'État physique', detail: `${etatLabels[bd.etatGeneral]||'Non renseigné'} → ×${coefEtat}`, type:'coef' },
  ];
  if (age>5) sources.push({ label:'Ancienneté', detail:`${age} an${age>1?'s':''} → ×${coefAge}`, type:'coef' });
  if (coefSpecial>1) {
    const sp=[];
    if(bd.eo) sp.push('1ère édition (×2.5)');
    if(bd.dedicace) sp.push('Dédicace (×2.2)');
    if(bd.numerote) sp.push('Numéroté (×1.6)');
    sources.push({ label:'Caractéristiques spéciales', detail:sp.join(' · '), type:'special' });
  }
  return { estimation:estim, fourchette:{low,high}, sources };
};


// ─── Sauvegarde / restauration complète ──────────────────────────────────────
const tsToISO = t =>
  t?.toDate ? t.toDate().toISOString()
  : t instanceof Date ? t.toISOString()
  : typeof t === 'string' ? t : null;

// Génère et télécharge un fichier JSON (BD + photos base64 + souhaits).
// Tout est déjà en mémoire (photos incluses dans chaque doc) : aucune lecture Firestore.
export const exportFullBackup = (bds, wishlist = []) => {
  const clean = ({ createdAt, updatedAt, ...rest }) => ({
    ...rest, createdAt: tsToISO(createdAt), updatedAt: tsToISO(updatedAt),
  });
  const payload = {
    app: 'Bulletheque',
    version: 1,
    exportedAt: new Date().toISOString(),
    bds: bds.map(clean),
    wishlist: wishlist.map(clean),
    series: { totals: loadTotals(), hs: loadHsTotals() },
  };
  const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `bulletheque_sauvegarde_${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  return { bds: bds.length, wishlist: wishlist.length, bytes: blob.size };
};

// Valide le contenu d'un fichier de sauvegarde (lève une erreur si invalide).
export const parseBackup = (text) => {
  let data;
  try { data = JSON.parse(text); } catch { throw new Error('Fichier illisible (JSON invalide).'); }
  if (data?.app !== 'Bulletheque' || !Array.isArray(data.bds))
    throw new Error("Ce fichier n'est pas une sauvegarde Bulléthèque.");
  return { bds: data.bds, wishlist: Array.isArray(data.wishlist) ? data.wishlist : [], series: data.series || null };
};

// Restauration : conserve les ids d'origine (relancer une restauration ne crée pas
// de doublons ; un doc existant avec le même id est remplacé) et garde les photos.
// Lots limités à 100 docs ET ~4 Mo (limite Firestore ≈ 10 Mo par commit, photos base64).
const restoreBatch = async (userId, sub, items, onProgress) => {
  const MAX_DOCS = 100, MAX_BYTES = 4 * 1024 * 1024;
  let done = 0, i = 0;
  while (i < items.length) {
    const batch = writeBatch(db);
    let bytes = 0, n = 0;
    while (i < items.length && n < MAX_DOCS) {
      const size = JSON.stringify(items[i]).length;
      if (n > 0 && bytes + size > MAX_BYTES) break;
      const { id, createdAt, updatedAt, ...rest } = items[i];
      const ref = id ? doc(db, 'users', userId, sub, id) : doc(collection(db, 'users', userId, sub));
      batch.set(ref, { ...rest, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
      bytes += size; n++; i++;
    }
    await batch.commit();
    done += n;
    onProgress?.(done);
  }
  return done;
};
export const restoreBDsBatch      = (userId, items, onProgress) => restoreBatch(userId, 'bds', items, onProgress);
export const restoreWishlistBatch = (userId, items, onProgress) => restoreBatch(userId, 'wishlist', items, onProgress);

// ─── Partage public en lecture seule (lien "collection partagée", sans compte) ──
// Contenu volontairement léger : pas de photos, juste les champs choisis par
// le propriétaire. Chaque génération crée un nouveau document (nouveau lien),
// pour ne jamais faire changer le contenu d'un lien déjà envoyé à quelqu'un.
export const SHARE_RETENTION_DAYS = 90;

export const createShare = async (userId, { title, items }) => {
  const ref = doc(collection(db, 'shares'));
  await setDoc(ref, {
    ownerId: userId,
    title: title || 'Ma collection',
    items,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    expiresAt: new Date(Date.now() + SHARE_RETENTION_DAYS * 24 * 60 * 60 * 1000),
  });
  return ref.id;
};

export const fetchShare = async (shareId) => {
  try {
    const snap = await getDoc(doc(db, 'shares', shareId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch {
    // Lecture refusée par les règles : lien expiré (ou inaccessible pour une autre raison)
    return null;
  }
};

// ─── Recherche par titre (utilisée par la reconnaissance de couverture) ──────
// Renvoie plusieurs candidats (la recherche par texte est plus floue qu'un ISBN exact).
export const searchByTitle = async (query) => {
  const clean = query.trim();
  if (!clean) return [];
  try {
    const r = await fetch(`https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent('intitle:'+clean)}&maxResults=5`);
    const d = await r.json();
    return (d.items || [])
      .map(it => mapGoogleVolume(it.volumeInfo))
      .filter(x => x.titre || x.serie);
  } catch { return []; }
};
